import React, { useState, useEffect, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Input, DatePicker, Button, Badge, Select, Drawer, Popconfirm } from 'antd';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from 'controllers/notificationController';
import moment from 'moment';
import { createClientAddressAction, deleteAddressOfClientAction, getAddressesOfClientAction, updateClientAddressAction, updateClientDefaultAddressAction } from '@app/store/slices/userSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { useNavigate, useParams } from 'react-router-dom';
import { useLoadScript, Autocomplete, GoogleMap, MarkerF } from '@react-google-maps/api';
import config from '@app/config';

const { TextArea } = Input;
const { Option } = Select;

// Map configuration
const mapContainerStyle = {
  width: '100%',
  height: '400px',
};

const center = {
  lat: 30.7333, // Default center (e.g., Panchkula)
  lng: 76.7794,
};

export const BasicTableClientAddresses: React.FC = () => {
  const [filters, setFilters] = useState({
    clientName: '',
    deliveryDateRange: [],
    deliveryStatus: '',
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [tableData, setTableData] = useState({ data: [], loading: false, total: 0 });
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<any>({});
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<any>(null);
  const [mapCenter, setMapCenter] = useState(center); // Center of the map
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);
  const [markerPosition, setMarkerPosition] = useState(center); // Marker position
  const [mapRefresh, setMapRefresh] = useState(2134);
  const [isFormValid, setIsFormValid] = useState(false); // Form validation state
  const { id } = useParams();
  const dispatch = useAppDispatch();
  let navigate = useNavigate();

  const [libraries] = useState(['places']);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries: libraries,
  });

  const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
    setAutocomplete(autocomplete);
  };

  const validateForm = (addressData?: any) => {
    const data = addressData || selectedAddress;
    const isAddressValid = data?.args?.address && data?.args?.city && data?.args?.pincode;
    const isHouseNoValid = data?.args?.house_no;
    const isTypeValid = data?.args?.type;
    //const isNoteValid = data?.args?.note;
    const isCustomerNameValid = data?.args?.customer_name;
    const isValid = !!(isAddressValid && isHouseNoValid && isTypeValid && isCustomerNameValid);
    
    console.log('validateForm called:', {
      isAddressValid,
      isHouseNoValid,
      isTypeValid,
      isCustomerNameValid,
      isValid,
      type: data?.args?.type
    });
    
    setIsFormValid(isValid);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      const addressComponents = place.address_components;
      const address = place.formatted_address || '';
      const city = addressComponents?.find((component: any) => component.types.includes('locality'))?.long_name || '';
      const pincode = addressComponents?.find((component: any) => component.types.includes('postal_code'))?.long_name || '';
      const lat = place.geometry?.location?.lat() || 0;
      const lng = place.geometry?.location?.lng() || 0;
      const sector = getSector(addressComponents);
      console.log('sector', sector);

      console.log('place', place);
      setMapCenter({ lat, lng });
      setMarkerPosition({ lat, lng });

      const payload = {
        args: {
          address: address,
          city: city,
          house_no: selectedAddress?.args?.house_no || '',
          lat: lat,
          long: lng,
          note: selectedAddress?.args?.note || '',
          pincode: pincode,
          type: selectedAddress?.args?.type || '',
          sector: sector
        },
      };

      setSelectedAddress(payload);
      setTimeout(() => {
        setMapRefresh(Math.random());
      }, 1000);

      validateForm();
    } else {
      console.log('Autocomplete is not loaded yet!');
    }
  };

  const showDrawer = (record: any) => {
    setCurrentRecord(record);
    setSelectedAddress({
      args: {
        address: record?.address || '',
        city: record?.city || '',
        house_no: record?.house_no || '',
        lat: record?.lat || 0,
        long: record?.long || 0,
        note: record?.note || '',
        pincode: record?.pincode || '',
        type: record?.type || '',
        customer_name: record?.customer_name || '',
        sector: record?.sector || '',
      },
    });
    setMapCenter({ lat: record?.lat || center.lat, lng: record?.long || center.lng });
    setMarkerPosition({ lat: record?.lat || center.lat, lng: record?.long || center.lng });
    setIsDrawerVisible(true);
    setIsFormValid(!!record); // If editing, form is valid; if adding, form is invalid
  };

  const closeDrawer = () => {
    setIsDrawerVisible(false);
    setSelectedAddress(null);
    setCurrentRecord(null);
  };

  const handleSaveAddress = () => {
    if (!selectedAddress?.args?.address || !selectedAddress?.args?.city || !selectedAddress?.args?.pincode) {
      notificationController.warning({ message: 'Please select a valid address from the dropdown.' });
      return;
    }

    if (!selectedAddress?.args?.house_no || !selectedAddress?.args?.type || !selectedAddress?.args?.customer_name) {
      notificationController.warning({ message: 'Please fill all mandatory fields.' });
      return;
    }

    if (currentRecord) {
      dispatch(updateClientAddressAction({ id: currentRecord._id, payload: selectedAddress?.args }))
        .unwrap()
        .then((resp) => {
          closeDrawer();
          if (resp.success) {
            notificationController.success({ message: 'Address updated successfully' });
            fetchData();
          } else {
            notificationController.warning({ message: resp?.message || "Something went wrong" });
          }
        })
        .catch((ex) => {
          notificationController.error({ message: ex?.message || 'Address update failed' });
          closeDrawer();
        });
    } else {
      let _selectedAddress = selectedAddress?.args;
      let args = {
        user_id: id,
        ..._selectedAddress,
      };
      dispatch(createClientAddressAction(args))
        .unwrap()
        .then((resp) => {
          closeDrawer();
          if (resp.success) {
            notificationController.success({ message: 'Address added successfully' });
            fetchData();
          } else {
            notificationController.warning({ message: resp?.message || "Something went wrong" });
          }
        })
        .catch((ex) => {
          notificationController.error({ message: ex?.message || 'Address addition failed' });
          closeDrawer();
        });
    }
  };

  const handleDefaultAddress = (addressId: string) => {
    dispatch(updateClientDefaultAddressAction({ clientId: id, addressId }))
      .unwrap()
      .then((resp) => {
        closeDrawer();
        if (resp.success) {
          notificationController.success({ message: 'Default Address updated successfully' });
          fetchData();
        } else {
          notificationController.warning({ message: resp?.message || "Something went wrong" });
        }
      })
      .catch((ex) => {
        notificationController.error({ message: ex?.message || 'Address update failed' });
        closeDrawer();
      });
  };

  const handleDeleteAddress = (addressId: string) => {
    dispatch(deleteAddressOfClientAction(addressId))
      .unwrap()
      .then((resp) => {
        closeDrawer();
        if (resp.success) {
          notificationController.success({ message: 'Default Address updated successfully' });
          fetchData();
        } else {
          notificationController.warning({ message: resp?.message || "Something went wrong" });
        }
      })
      .catch((ex) => {
        notificationController.error({ message: ex?.message || 'Address update failed' });
        closeDrawer();
      });
  };


  const onMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
    const lat = e.latLng?.lat() || 0;
    const lng = e.latLng?.lng() || 0;
    setMarkerPosition({ lat, lng });

    // Reverse geocode the new position
    reverseGeocode(lat, lng);
  };

  const fetchData = useCallback(() => {
    setTableData((prev) => ({ ...prev, loading: true }));

    const params = {
      ...filters,
      deliveryDateRange: filters.deliveryDateRange.length
        ? [
          moment(filters.deliveryDateRange[0]).toISOString(),
          moment(filters.deliveryDateRange[1]).toISOString(),
        ]
        : [],
      current: pagination.current,
      pageSize: pagination.pageSize,
    };

    dispatch(getAddressesOfClientAction(id))
      .unwrap()
      .then((response) => {
        setTableData({
          data: response.data,
          loading: false,
          total: response.total,
        });
      })
      .catch((error) => {
        notificationController.error({ message: error.message || 'Failed to fetch data' });
        setTableData((prev) => ({ ...prev, loading: false }));
      });
  }, [pagination, dispatch]);

  useEffect(() => {
    function hideError(e) {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    }

    window.addEventListener('error', hideError);
    return () => {
      window.removeEventListener('error', hideError);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (filterName: string, value: any) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  const handleTableChange = (paginationInfo) => {
    setPagination({ current: paginationInfo.current, pageSize: paginationInfo.pageSize });
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Customer Name.',
      dataIndex: 'customer_name',
    },
    {
      title: 'House No.',
      dataIndex: 'house_no',
    },
    {
      title: 'Address',
      dataIndex: 'address',
    },
    {
      title: 'Pincode',
      dataIndex: 'pincode',
    },
    {
      title: 'City',
      dataIndex: 'city',
    },
    {
      title: 'Lat',
      dataIndex: 'lat',
    },
    {
      title: 'Long',
      dataIndex: 'long',
    },
    {
      title: 'Sector',
      dataIndex: 'sector',
    },
    {
      title: 'Is Default',
      dataIndex: 'isDefault',
      render: (_text, record) => (record.isDefault ? 'Yes' : 'No'),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      render: (text, record) => (
        <div style={{ flexDirection: 'row', display: 'flex', gap: 10 }}>
          <Button type="primary" onClick={() => showDrawer(record)}>
            Edit Address
          </Button>

          <Popconfirm
            placement="topLeft"
            title={'Are you sure you want to set this address as default?'}
            onConfirm={(evt) => handleDefaultAddress(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary">Mark as Default</Button>
          </Popconfirm>


          <Popconfirm
            placement="topLeft"
            title={'Are you sure you want to delete this address?'}
            onConfirm={(evt) => handleDeleteAddress(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary">Delete</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (isLoaded) {
      setGeocoder(new google.maps.Geocoder());
    }
  }, [isLoaded]);


  function getSector(addressComponents: any) {
    for (const component of addressComponents) {
      if (component.types.includes("sublocality")) {
        return `Sector ${component.long_name}`;
      }
    }
    return null;
  }
  const reverseGeocode = (lat: number, lng: number) => {
    if (!geocoder) return;

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const addressComponents = results[0].address_components;
        const address = results[0].formatted_address;
        const sector = getSector(addressComponents);
        const city = addressComponents.find((component: any) =>
          component.types.includes('locality')
        )?.long_name;
        const pincode = addressComponents.find((component: any) =>
          component.types.includes('postal_code')
        )?.long_name;


        console.log('sector', sector);
        setSelectedAddress((prev: any) => ({
          args: {
            ...prev.args,
            address: address,
            city: city,
            pincode: pincode,
            lat: lat,
            long: lng,
            sector: sector
          },
        }));
      } else {
        console.error('Geocoder failed due to: ', status);
      }
    });
  };

  const handleInputChange = (field: string, value: string) => {
    console.log('handleInputChange called:', { field, value });
    setSelectedAddress((prev: any) => {
      const updatedAddress = {
        args: {
          ...prev?.args,
          [field]: value,
        },
      };
      console.log('Updated address:', updatedAddress);
      // Validate with the updated data immediately
      validateForm(updatedAddress);
      return updatedAddress;
    });
  };

  return (
    <>
      <div style={{ flexDirection: 'row', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Button
          style={{ marginBottom: '20px', float: 'right' }}
          onClick={() => {
            setIsDrawerVisible(true);
          }}
        >
          Add New
        </Button>
        <Button
          style={{ marginBottom: '20px', float: 'right' }}
          onClick={() => {
            navigate(-1);
          }}
        >
          Back
        </Button>
      </div>
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: tableData.total,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        bordered
      />

      <Drawer
        title="Edit Address"
        onClose={closeDrawer}
        size="large"
        visible={isDrawerVisible}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={closeDrawer} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button onClick={handleSaveAddress} type="primary" disabled={!isFormValid}>
              Save
            </Button>
          </div>
        }
      >
        {isLoaded && (
          <>
            <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
              <Input
                placeholder="Enter address"
                style={{ width: '100%', marginBottom: '20px' }}
              />
            </Autocomplete>
            <GoogleMap
              key={mapRefresh} // Force re-render when center changes
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={15}
            >
              <MarkerF
                position={markerPosition}
                draggable={true}
                onDragEnd={onMarkerDragEnd}
              />
            </GoogleMap>
            {true && (
              <div style={{ marginTop: '20px' }}>

                <label>Customer Name.</label>
                <Input
                  placeholder="Customer Name"
                  value={selectedAddress?.args?.customer_name}
                  onChange={(e) => handleInputChange('customer_name', e.target.value)}
                  style={{ marginBottom: '20px' }}
                />

                <label>House No.</label>
                <Input
                  placeholder="House No."
                  value={selectedAddress?.args?.house_no}
                  onChange={(e) => handleInputChange('house_no', e.target.value)}
                  style={{ marginBottom: '20px' }}
                />
                <label>Address</label>
                <Input
                  placeholder="Address"
                  value={selectedAddress?.args?.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  style={{ marginBottom: '20px' }}
                />
                <label>Type</label>
                <Select
                  placeholder="Type"
                  value={selectedAddress?.args?.type}
                  onChange={(value) => handleInputChange('type', value)}
                  style={{ width: '100%', marginBottom: '20px' }}
                >
                  <Option value="Home">Home</Option>
                  <Option value="Office">Office</Option>
                  <Option value="Other">Other</Option>
                </Select>
                <label>Note</label>
                <TextArea
                  placeholder="Note"
                  value={selectedAddress?.args?.note}
                  onChange={(e) => handleInputChange('note', e.target.value)}
                  style={{ marginBottom: '20px' }}
                />
              </div>
            )}
          </>
        )}
      </Drawer>
    </>
  );
};